/**
 * Path segments, route names and query keys for the my-pages (trainer portal) area, mounted under
 * `mypages/o/:orgIdentifier/...` in `routes.js`.
 *
 * Same rule as `organizationPaths.js`: define each value once here and reference it from the route
 * definition and from every link builder (`urlUtil.getLocalisedMyPagesUrl(MyPagesPath.SETTINGS)`),
 * so a rename is one edit and grep finds every use.
 *
 * Settings section pages live under `settings/<section>`. The section list itself (icons, status,
 * summaries) is domain data in `src/domains/settings/settingsSections.js`; this file only owns the
 * URL shape.
 */
/**
 * The logged-in person's own settings: name, email, password, delete account.
 *
 * A full path, not a segment like the values in `MyPagesPath`: this route is mounted directly
 * under `/mypages`, outside `o/:orgIdentifier`, so it still resolves for a login that has no
 * studio account yet. Route name `UserProfileSettings` in `routes.js`.
 */
export const USER_PROFILE_PATH = '/mypages/account'

export const MyPagesPath = {
  EVENTS: 'events',
  PARTICIPANTS: 'participants',
  MEMBERSHIPS: 'memberships',
  CLASSES: 'classes',
  MEMBERSHIP_TEMPLATES: 'membership-templates',
  MY_SITE: 'mysite',
  SETTINGS: 'settings',
  SETTINGS_GENERAL_RULES: 'settings/general-rules',
  SETTINGS_SERVICES_PRICING: 'settings/services-pricing',
  SETTINGS_ONLINE_PAYMENTS: 'settings/online-payments',
  SETTINGS_TEAM: 'settings/team',
  SETTINGS_ACCOUNT: 'settings/account',
  /** Legacy paths kept only as redirects. */
  LEGACY_MEMBERSHIP_SETTINGS: 'membership-settings',
  LEGACY_SERVICES_SETTINGS: 'services-settings'
}

export const MyPagesRouteName = {
  EVENTS: 'MyEvents',
  PARTICIPANTS: 'MyParticipants',
  MEMBERSHIPS: 'Memberships',
  CLASSES: 'MyClasses',
  MEMBERSHIP_TEMPLATES: 'MembershipTemplates',
  MY_SITE: 'MySite',
  SETTINGS: 'MySettings',
  SETTINGS_GENERAL_RULES: 'SettingsGeneralRules',
  SETTINGS_SERVICES_PRICING: 'SettingsServicesPricing',
  SETTINGS_ONLINE_PAYMENTS: 'SettingsOnlinePayments',
  SETTINGS_TEAM: 'SettingsTeam',
  SETTINGS_ACCOUNT: 'SettingsAccount',
  LEGACY_MEMBERSHIP_SETTINGS: 'MyMembershipSettings',
  LEGACY_SERVICES_SETTINGS: 'ServicesSettings'
}

/**
 * `?tab=` query values the old Konta iestatījumi page understood. Kept only so old links and
 * bookmarks still land on the right section page: `SettingsIndexView` redirects them.
 */
export const LegacySettingsTab = {
  QUERY_KEY: 'tab',
  PLAN: 'plan',
  COMPANY_DETAILS: 'company-details',
  MODULES: 'modules',
  CONFIGURATION: 'configuration',
  OTHER: 'other'
}

/** Old `?tab=` value → the route name of the page that now holds that content. */
export const LEGACY_SETTINGS_TAB_ROUTE = {
  [LegacySettingsTab.PLAN]: MyPagesRouteName.SETTINGS_ACCOUNT,
  [LegacySettingsTab.COMPANY_DETAILS]: MyPagesRouteName.SETTINGS_ACCOUNT,
  [LegacySettingsTab.MODULES]: MyPagesRouteName.SETTINGS_ONLINE_PAYMENTS,
  [LegacySettingsTab.CONFIGURATION]: MyPagesRouteName.SETTINGS_GENERAL_RULES,
  [LegacySettingsTab.OTHER]: MyPagesRouteName.SETTINGS_ACCOUNT
}

/**
 * The participant portal, mounted at `mypages/participant/...` — a different area from the trainer
 * portal above (which is org-scoped), so it gets its own constants rather than sharing `MyPagesPath`.
 *
 * `Segment` values are what the route definitions in `routes.js` use; the absolute paths are what
 * redirects use (router guard, post-signup landing) and are built from the same segments.
 */
export const ParticipantSegment = {
  ROOT: 'participant',
  DASHBOARD: 'participant-dashboard',
  /** "Rēķini" — the participant's own payments of the last 90 days, with receipts. */
  PURCHASES: 'purchases',
  EMAIL_VERIFICATION: 'email-verification'
}

export const MY_PAGES_ROOT_PATH = '/mypages'

const PARTICIPANT_ROOT_PATH = `${MY_PAGES_ROOT_PATH}/${ParticipantSegment.ROOT}`

export const ParticipantPath = {
  ROOT: PARTICIPANT_ROOT_PATH,
  /** "Mani pieraksti" — the participant's own signups, and where a participant belongs by default. */
  DASHBOARD: `${PARTICIPANT_ROOT_PATH}/${ParticipantSegment.DASHBOARD}`,
  EMAIL_VERIFICATION: `${PARTICIPANT_ROOT_PATH}/${ParticipantSegment.EMAIL_VERIFICATION}`
}

export const ParticipantRouteName = {
  DASHBOARD_ROOT: 'ParticipantDashboardPath',
  DASHBOARD: 'ParticipantDashboard',
  PURCHASES: 'ParticipantPurchases',
  EMAIL_VERIFICATION: 'EmailVerification'
}

/** Trainer-portal account root: `mypages/o/<orgIdentifier>/...`. */
export const MY_PAGES_ORGANIZATION_ROOT_PATH = `${MY_PAGES_ROOT_PATH}/o`
